// src/components/auth/EmailActionHandler.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../firebase/firebase';
import { 
  applyActionCode, 
  confirmPasswordReset, 
  verifyPasswordResetCode 
} from 'firebase/auth';
import Modal from '../ui/Modal';
import InputField from '../ui/InputField';
import AnimatedCharacter from '../ui/AnimatedCharacter';
import { Eye, EyeClosed } from 'lucide-react';

/**
 * EmailActionHandler Component
 * Handles email verification and password reset from Firebase email links
 * Uses Modal with AnimatedCharacter to match AuthPage styling
 */
const EmailActionHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState(null); // 'resetPassword' or 'verifyEmail'
  const [actionCode, setActionCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Password reset specific state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordValid, setPasswordValid] = useState(true);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Blink sequence for AnimatedCharacter (static mode)
  const blinkSequence = [
    {
      x: 0,
      y: 0,
      duration: 0.1,
      pause: 200,
      postAnimationPause: 0,
      specialAnimation: 'blink'
    },
    {
      x: 0,
      y: 0,
      duration: 0.1,
      pause: 5000,
      postAnimationPause: 0,
      specialAnimation: null
    }
  ];

  // Parse URL parameters on mount
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    const codeParam = searchParams.get('oobCode');
    
    console.log('EmailActionHandler - mode:', modeParam, 'code:', codeParam);
    
    if (!modeParam || !codeParam) {
      setError('Invalid or expired link. Please request a new one.');
      setLoading(false);
      return;
    }
    
    setMode(modeParam);
    setActionCode(codeParam);
    
    // For email verification, process immediately
    if (modeParam === 'verifyEmail') {
      handleEmailVerification(codeParam);
    } else if (modeParam === 'resetPassword') {
      // For password reset, verify the code is valid first
      verifyResetCode(codeParam);
    } else {
      setError('Unknown action type.');
      setLoading(false);
    }
  }, [searchParams]);

  // Verify password reset code is valid
  const verifyResetCode = async (code) => {
    try {
      await verifyPasswordResetCode(auth, code);
      setLoading(false);
    } catch (err) {
      console.error('Error verifying reset code:', err);
      setError('This password reset link is invalid or has expired. Please request a new one.');
      setLoading(false);
    }
  };

  // Handle email verification
  const handleEmailVerification = async (code) => {
    try {
      await applyActionCode(auth, code);
      console.log('Email verified successfully');
      
      // Notify other tabs via BroadcastChannel
      try {
        const channel = new BroadcastChannel('email-verification');
        channel.postMessage('verified');
        channel.close();
        console.log('EmailActionHandler - Broadcasted verification to other tabs');
      } catch (broadcastErr) {
        console.error('BroadcastChannel not supported or error:', broadcastErr);
        // Continue anyway - the user can still use the button
      }
      
      setSuccess(true);
      setLoading(false);
      
      // Don't auto-redirect - let the original tab handle login
      // User can close this tab manually
    } catch (err) {
      console.error('Error verifying email:', err);
      
      if (err.code === 'auth/invalid-action-code') {
        setError('This verification link is invalid or has already been used.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This verification link has expired. Please request a new one.');
      } else {
        setError('Failed to verify email. Please try again or contact support.');
      }
      
      setLoading(false);
    }
  };
  
  // Handle password reset form submission
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    // Validation
    let hasError = false;
    
    if (newPassword.length < 8) {
      setPasswordTouched(true);
      setPasswordValid(false);
      hasError = true;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordsMatch(false);
      hasError = true;
    }
    
    if (hasError) {
      return;
    }
    
    try {
      setError('');
      setProcessing(true);
      
      await confirmPasswordReset(auth, actionCode, newPassword);
      console.log('Password reset successfully');
      
      setSuccess(true);
      
      // Redirect to app after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error resetting password:', err);
      
      if (err.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid or has already been used.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This password reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Please choose a stronger password.');
      } else {
        setError('Failed to reset password. Please try again or contact support.');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Password blur handler
  const handlePasswordBlur = () => {
    setPasswordTouched(true);
    setPasswordValid(newPassword.length >= 8);
  };

  // Live validation - clear errors once fixed
  useEffect(() => {
    if (passwordTouched && newPassword.length >= 8) {
      setPasswordValid(true);
    }
  }, [newPassword, passwordTouched]);

  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(newPassword === confirmPassword);
    }
  }, [newPassword, confirmPassword]);

  // Render loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-app-gray-800">
        <Modal
          isOpen={true}
          onClose={() => {}}
          title="Processing..."
          showCloseButton={false}
          width="400px"
          headerContent={
            <div className="w-full h-12">
              <AnimatedCharacter
                lookAtSequence={blinkSequence}
                staticMode={true}
                className="w-full h-12"
              />
            </div>
          }
          titleAlign="center"
          titleClassName="font-semibold"
          contentPadding="px-app-3xl pb-app-2xl pt-app-xl"
        >
          <div className="flex flex-col items-center justify-center py-app-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-gray-900 mb-app-lg"></div>
            <p className="text-app-sm text-app-gray-600">Please wait...</p>
          </div>
        </Modal>
      </div>
    );
  }

  // Render email verification success
  if (mode === 'verifyEmail' && success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-app-gray-800">
        <Modal
          isOpen={true}
          onClose={() => {}}
          title="Email Verified!"
          showCloseButton={false}
          width="400px"
          headerContent={
            <div className="w-full h-12">
              <AnimatedCharacter
                lookAtSequence={blinkSequence}
                staticMode={true}
                className="w-full h-12"
              />
            </div>
          }
          titleAlign="center"
          titleClassName="font-semibold"
          contentPadding="pt-app-xl"
        >
          <div className="text-center">
            <div className="mb-app-2xl">
              <svg className="w-16 h-16 mx-auto text-green-500 mb-app-md" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-app-md font-medium text-app-gray-700 mb-app-sm">
                You can safely close this tab.
              </p>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // Render password reset success
  if (mode === 'resetPassword' && success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-app-gray-800">
        <Modal
          isOpen={true}
          onClose={() => {}}
          title="Password Reset!"
          showCloseButton={false}
          width="400px"
          headerContent={
            <div className="w-full h-12">
              <AnimatedCharacter
                lookAtSequence={blinkSequence}
                staticMode={true}
                className="w-full h-12"
              />
            </div>
          }
          titleAlign="center"
          titleClassName="font-semibold"
          contentPadding="px-app-3xl pb-app-2xl pt-app-xl"
        >
          <div className="flex flex-col items-center py-app-xl">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-app-lg">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-app-sm text-app-gray-600 text-center mb-app-lg">
              Your password has been reset successfully!
            </p>
            <p className="text-app-xs text-app-gray-500">
              Redirecting you to the app...
            </p>
          </div>
        </Modal>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-app-gray-800">
        <Modal
          isOpen={true}
          onClose={() => {}}
          showCloseButton={false}
          title="Error"
          width="400px"
          headerContent={
            <div className="w-full h-12">
              <AnimatedCharacter
                lookAtSequence={blinkSequence}
                staticMode={true}
                className="w-full h-12"
              />
            </div>
          }
          titleAlign="center"
          titleClassName="font-semibold"
          contentPadding="px-app-3xl pb-app-2xl pt-app-xl"
        >
          <div className="py-app-lg">
            <div className="bg-red-100 border border-red-400 text-red-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
              {error}
            </div>
            <button
              onClick={() => navigate('/')}
              className="btn-primary w-full"
            >
              Return to Login
            </button>
          </div>
        </Modal>
      </div>
    );
  }

  // Render password reset form
  if (mode === 'resetPassword') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-app-gray-800">
        <Modal
          isOpen={true}
          onClose={() => {}}
          title="Reset Your Password"
          showCloseButton={false}
          width="400px"
          headerContent={
            <div className="w-full h-12">
              <AnimatedCharacter
                lookAtSequence={blinkSequence}
                staticMode={true}
                className="w-full h-12"
              />
            </div>
          }
          titleAlign="center"
          titleClassName="font-semibold"
          contentPadding="px-app-3xl pb-app-2xl pt-app-xl"
        >
          <form onSubmit={handlePasswordReset} noValidate>
            <p className="text-app-sm text-app-gray-600 mb-app-xl">
              Enter your new password below.
            </p>

            <div className="space-y-app-xs mb-app-2xl">
              <InputField
                id="newPassword"
                label="New Password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onBlur={handlePasswordBlur}
                placeholder="Enter new password"
                required
                disabled={processing}
                showPasswordToggle={true}
                validationMessage="Password must be at least 8 characters"
                validationMode="permanent" // Always shows, gray when valid, red when invalid
                validationCheck="blur" // Check on blur, clear live once valid
                isValid={passwordValid || newPassword === ''}
              />

              <InputField
                id="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={processing}
                showPasswordToggle={true}
                validationMessage="Passwords do not match"
                validationMode="inline" // Only shows when invalid
                validationCheck="live" // Check as you type
                isValid={passwordsMatch || confirmPassword === ''}
              />
            </div>

            <button
              type="submit"
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={processing || !newPassword || !confirmPassword || newPassword.length < 8 || newPassword !== confirmPassword}
            >
              {processing ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        </Modal>
      </div>
    );
  }

  return null;
};

export default EmailActionHandler;